import { hasPermission } from '@/hooks/useBackendMenu'
import { CategoriaEnum } from '@acme/auth'
import { OriginProps, parserRegisterOrigin } from '@acme/common'
import { SN, StatusAprovacao } from '@acme/core'
import { Button } from '@/global/components/Button'
import CircularProgress from '@/global/components/CircularProgress'
import Dialog from 'global/guides/Dialog'
import { useAuth } from 'hooks'
import { PrinterIcon } from 'lucide-react'
import { useCallback, useState } from 'react'
import { useParams } from 'react-router-dom'
import { ErrorHandler, Registro, registros as registrosApi } from 'service/api'
import { toast } from 'sonner'
import EntradaDiaSeguinte from '../atoms/entrada-dia-seguinte'
import SaidaDiaAnterior from '../atoms/saida-dia-anterior'
import { FormularioDetalhesRegistro } from '../forms/FormDetalhesRegistro'
import { useDigitacaoContext } from '../pages/DigitacaoLayout'
import { useUpdateDigitacaoQueryData } from '../pages/useDigitacaoData'
import useService from '../service'

type FnHandle = {
    (data: { id: number; motivo: string }): void
    (data: { id: number }): void
}

export function DetalhesRegistro() {
    const { decoded } = useAuth()
    const { handleAprovar, handleReprovar, handleExcluir } = useService()
    const { registroId } = useParams()
    const { data } = useDigitacaoContext()
    const registros = data?.flatMap(jornada => jornada.registros)
    const { setQueryData } = useUpdateDigitacaoQueryData()
    const registro = registros?.find(
        registro => registro.id === Number(registroId),
    )
    const { title, exibirComprovante } = parserRegisterOrigin(
        registro?.origem!,
    ) as OriginProps
    const [isImprimindo, setIsImprimindo] = useState(false)
    const [saidaDiaAnterior, setSaidaDiaAnterior] = useState(
        registro?.saidaDiaAnterior,
    )
    const [entradaDiaSeguinte, setEntradaDiaSeguinte] = useState(
        registro?.entradaDiaSeguinte,
    )
    const { mutateAsync } = registrosApi.mutations.useUpdate()

    const [isLoading, setIsLoading] = useState(false)

    async function updateRegistro(values: Registro) {
        setIsLoading(true)
        return mutateAsync({
            ...values,
            id: registro?.id!,
        }).finally(() => setIsLoading(false))
    }

    const handleClickComprovante = async () => {
        setIsImprimindo(true)
        registrosApi
            .pdf({
                queryParam: { id: registro?.id },
            })
            .finally(() => setIsImprimindo(false))
    }

    const handleClickExcluir: FnHandle = data => {
        handleExcluir('REGISTRO', {
            ...registro,
            ...data,
        })
    }

    const toggle = useCallback(
        async (tipo: 'entrada-dia-seguinte' | 'saida-dia-anterior') => {
            const value =
                tipo === 'entrada-dia-seguinte'
                    ? entradaDiaSeguinte
                    : saidaDiaAnterior

            const toggle = value === 'S' ? 'N' : 'S'

            const msgAtualizar =
                tipo === 'entrada-dia-seguinte'
                    ? 'Entrada no dia seguinte atualizada'
                    : 'Saida no dia anterior atualizada'

            const msgRemover =
                tipo === 'entrada-dia-seguinte'
                    ? 'Entrada no dia seguinte removida'
                    : 'Saida no dia anterior removida'

            try {
                setIsLoading(true)

                const registro = await registrosApi.patchFrom(
                    registroId!,
                    tipo,
                    {},
                )

                if (tipo === 'entrada-dia-seguinte') {
                    setEntradaDiaSeguinte(toggle)
                } else {
                    setSaidaDiaAnterior(toggle)
                }

                setQueryData('REGISTRO', registro)

                if (toggle === 'S') {
                    toast.success(msgAtualizar)
                    return
                } else {
                    toast.success(msgRemover)
                }
            } catch (error) {
                const { message } = error as ErrorHandler
                toast.error(message)
            } finally {
                setIsLoading(false)
            }
        },
        [saidaDiaAnterior, entradaDiaSeguinte, isLoading],
    )

    const funcionarioPodeAjustar = funcionarioPodeSolicitarAjustes()

    if (!registro) return null

    return (
        <Dialog.Root
            disableFields={!hasPermission(['update', 'Registros'])}
            defaultValues={{
                ...registro,
                origem: title,
                endereco: registro?.Local?.endereco,
            }}
            onDelete={
                hasPermission(['delete', 'Registros']) || funcionarioPodeAjustar
                    ? handleClickExcluir
                    : undefined
            }
            {...(hasPermission(['update', 'Registros']) && {
                onSubmit: updateRegistro,
            })}
            validateOnDelete={
                registro?.statusAprovacao === StatusAprovacao.APROVADO
            }
        >
            <Dialog.Title>
                Detalhes do registro
                {exibirComprovante && (
                    <Button
                        variant="outlined"
                        startIcon={
                            isImprimindo ? (
                                <CircularProgress size={20} />
                            ) : (
                                <PrinterIcon />
                            )
                        }
                        onClick={handleClickComprovante}
                    >
                        comprovante
                    </Button>
                )}
            </Dialog.Title>
            <Dialog.Content dividers>
                <FormularioDetalhesRegistro />
                <Dialog.Content.Footer
                    onApprove={
                        hasPermission(['update', 'Registros'])
                            ? () => handleAprovar('REGISTRO', registro)
                            : undefined
                    }
                    onReject={
                        hasPermission(['update', 'Registros'])
                            ? ({ motivo }) =>
                                  handleReprovar('REGISTRO', {
                                      id: registro.id,
                                      motivo,
                                  })
                            : undefined
                    }
                    tipoOcorrencia="REGISTRO"
                >
                    {hasPermission(['update', 'Registros'], () => [
                        <SaidaDiaAnterior
                            key="saida-dia-anterior"
                            categoria={decoded?.categoria!}
                            entradaDiaSeguinte={entradaDiaSeguinte}
                            saidaDiaAnterior={saidaDiaAnterior}
                            isLoading={isLoading}
                            toggle={toggle}
                        />,
                        <EntradaDiaSeguinte
                            key="entrada-dia-seguinte"
                            categoria={decoded?.categoria!}
                            entradaDiaSeguinte={entradaDiaSeguinte}
                            saidaDiaAnterior={saidaDiaAnterior}
                            isLoading={isLoading}
                            toggle={toggle}
                        />,
                    ])}
                </Dialog.Content.Footer>
            </Dialog.Content>
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton />
                <Dialog.Actions.PrimaryButton />
            </Dialog.Actions>
        </Dialog.Root>
    )
}

export function funcionarioPodeSolicitarAjustes() {
    const { decoded, funcionarioSelecionado } = useAuth()
    return (
        decoded.categoria === CategoriaEnum.Funcionario &&
        SN.isTrue(funcionarioSelecionado?.ajustesPonto)
    )
}
